import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiClipboard, FiAlertTriangle, FiClock, FiCheckCircle, FiXCircle, FiPackage
} from 'react-icons/fi';
import api from '../../../services/api';
import { getSettings } from '../../../models/settingsModel';

function HODDashboard({ user, dashboardData }) {
  const [helpdeskApprovals, setHelpdeskApprovals] = useState([]);
  const [loadingHelpdesk, setLoadingHelpdesk] = useState(true);
  const [assetDeclarationApprovals, setAssetDeclarationApprovals] = useState([]);
  const [loadingAssetDeclarations, setLoadingAssetDeclarations] = useState(true);
  const [hodApprovalEnabled, setHodApprovalEnabled] = useState(true); // Default to true, hide if explicitly disabled

  // Fetch helpdesk tickets pending my approval (uses backend delegation logic)
  useEffect(() => {
    const fetchHelpdeskApprovals = async () => {
      try {
        setLoadingHelpdesk(true);
        
        // Use the dedicated endpoint that handles delegation logic in the backend
        // This ensures delegates see tickets waiting for the original approver
        const response = await api.get('/api/helpdesk/tickets/my-pending-approvals');
        console.log('My pending helpdesk approvals response:', response.data);
        
        if (response.data.status === 1 && response.data.data) {
          const tickets = response.data.data.tickets || [];
          console.log('Tickets pending my approval:', tickets.length);
          setHelpdeskApprovals(tickets);
        } else {
          console.log('Helpdesk API returned non-success status:', response.data);
          setHelpdeskApprovals([]);
        }
      } catch (error) {
        console.error('Error fetching helpdesk approvals:', error);
        setHelpdeskApprovals([]);
      } finally {
        setLoadingHelpdesk(false);
      }
    };

    fetchHelpdeskApprovals();
  }, [user]);

  // Fetch settings to check if HOD approval for asset declarations is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        if (response.status === 1 && response.data) {
          // Check if HOD approval is enabled
          // Default to true if setting is not set (undefined/null)
          const settingValue = response.data.asset_declaration_hod_approval;
          const enabled = settingValue === undefined || settingValue === null || 
                         settingValue === '1' || settingValue === 1;
          setHodApprovalEnabled(enabled);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Default to true on error
        setHodApprovalEnabled(true);
      }
    };

    fetchSettings();
  }, []);

  // Fetch pending asset declaration approvals for HOD
  useEffect(() => {
    const fetchAssetDeclarationApprovals = async () => {
      try {
        setLoadingAssetDeclarations(true);
        const response = await api.get('/assets/pending-hod-approvals');
        
        if (response.data.status === 1) {
          setAssetDeclarationApprovals(response.data.data?.declarations || []);
        } else {
          setAssetDeclarationApprovals([]);
        }
      } catch (error) {
        console.error('Error fetching asset declaration approvals:', error);
        setAssetDeclarationApprovals([]);
      } finally {
        setLoadingAssetDeclarations(false);
      }
    };

    // Only fetch if user is HOD and HOD approval is enabled
    if ((user?.hod === 1 || user?.hod === '1') && hodApprovalEnabled) {
      fetchAssetDeclarationApprovals();
    } else {
      setLoadingAssetDeclarations(false);
      setAssetDeclarationApprovals([]);
    }
  }, [user, hodApprovalEnabled]);

  // Helper function to check if ticket is awaiting current user's approval
  // Also checks if user is a delegate for any approver
  const isAwaitingMyApproval = (ticket) => {
    if (!user || !ticket) return false;

    // Parse ticket_approvers if it's a string
    let approvers = [];
    try {
      approvers = typeof ticket.ticket_approvers === 'string' 
        ? JSON.parse(ticket.ticket_approvers) 
        : (ticket.ticket_approvers || []);
    } catch (e) {
      return false;
    }

    if (!Array.isArray(approvers) || approvers.length === 0) return false;

    // Get user properties
    const userId = parseInt(user.user_id || user.id);
    const userHod = parseInt(user.hod || 0);
    const userItSupport = parseInt(user.it_support || 0);
    const userManager = parseInt(user.manager || 0);
    const userSupervisor = parseInt(user.supervisor || 0);
    const userDirector = parseInt(user.director || 0);
    const userDepartment = parseInt(user.department || user.department_id || 0);
    const ticketDepartment = parseInt(ticket.requester_department || 0);

    // Check approval status fields to determine next pending approver
    const approvalStatus = {
      manager: parseInt(ticket.ticket_manager || 0),
      hod: parseInt(ticket.ticket_hod || 0),
      it_hod: parseInt(ticket.ticket_it_hod || 0),
      director: parseInt(ticket.ticket_director || 0)
    };

    // Find the next pending approval level
    let nextApproverType = null;
    for (const approverType of approvers) {
      // Check if this level is NOT yet approved (status = 0)
      if (approverType === 'manager' && approvalStatus.manager === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType.startsWith('hod_')) {
        // Specific HOD approval (uses ticket_hod field)
        if (approvalStatus.hod === 0) {
          nextApproverType = approverType;
          break;
        }
      } else if (approverType === 'hod' && approvalStatus.hod === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType === 'it_hod' && approvalStatus.it_hod === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType === 'director' && approvalStatus.director === 0) {
        nextApproverType = approverType;
        break;
      }
    }

    if (!nextApproverType) return false;

    // Match approver type to current user (directly or as delegate)
    let isMatch = false;
    
    if (nextApproverType.startsWith('hod_')) {
      // Specific HOD: hod_65 means only user 65 who is HOD (or their delegate)
      const hodUserId = parseInt(nextApproverType.substring(4));
      isMatch = userId === hodUserId && userHod === 1;
      
      // Also check if current user is the delegate for this specific HOD
      if (!isMatch && ticket.hods) {
        const specificHod = ticket.hods.find(h => parseInt(h.user_id) === hodUserId);
        if (specificHod?.delegate?.user_id && parseInt(specificHod.delegate.user_id) === userId) {
          isMatch = true;
        }
      }
    } else if (nextApproverType === 'manager') {
      // Manager or supervisor in same department
      isMatch = (userManager === 1 || userSupervisor === 1) && userDepartment === ticketDepartment;
      
      // Also check if user is delegate for the manager
      if (!isMatch && ticket.manager?.delegate?.user_id && parseInt(ticket.manager.delegate.user_id) === userId) {
        isMatch = true;
      }
    } else if (nextApproverType === 'hod') {
      // ANY HOD regardless of department (NO department check)
      isMatch = userHod === 1;
      
      // Also check if user is delegate for the HOD
      if (!isMatch && ticket.hod?.delegate?.user_id && parseInt(ticket.hod.delegate.user_id) === userId) {
        isMatch = true;
      }
    } else if (nextApproverType === 'it_hod') {
      // IT HOD: user must have BOTH hod=1 AND it_support=1 (NO department check)
      isMatch = userHod === 1 && userItSupport === 1;
      
      // Also check if user is delegate for the IT HOD
      if (!isMatch && ticket.it_hod?.delegate?.user_id && parseInt(ticket.it_hod.delegate.user_id) === userId) {
        isMatch = true;
      }
    } else if (nextApproverType === 'director') {
      // Director
      isMatch = userDirector === 1;
      
      // Also check if user is delegate for the director
      if (!isMatch && ticket.director?.delegate?.user_id && parseInt(ticket.director.delegate.user_id) === userId) {
        isMatch = true;
      }
    }

    return isMatch;
  };

  // Helper function to check if current user is the approver at this stage (including delegates)
  const isUserCurrentApprover = (req) => {
    if (!user || !user.user_id) return false;

    const normalizeUserId = (value) => {
      if (value === null || value === undefined) return null;
      const raw = typeof value === 'object' ? (value.user_id ?? value.id) : value;
      if (raw === null || raw === undefined) return null;
      const num = Number(raw);
      if (!Number.isFinite(num) || num <= 0) return null;
      return String(num);
    };

    const userId = normalizeUserId(user.user_id);
    if (!userId) return false;
    const reqStatus = Number(req.requisition_status);
    
    // Check if it's the user's turn based on status
    if (reqStatus === 0) {
      // First approval stage
      const delegatedTo = normalizeUserId(req.mid_delegated_to_user_id) ?? normalizeUserId(req.mid_delegate);
      const effectiveApprover = normalizeUserId(req.mid_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.mid);
      return !!effectiveApprover && effectiveApprover === userId;
    } else if (reqStatus === 1) {
      // Second approval stage
      const delegatedTo = normalizeUserId(req.second_delegated_to_user_id) ?? normalizeUserId(req.second_delegate);
      const effectiveApprover = normalizeUserId(req.second_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.second);
      return !!effectiveApprover && effectiveApprover === userId;
    } else if (reqStatus === 2 && req.specification_approvals >= 3) {
      // Third approval stage (only if required)
      // Check if quote exceeds site limit to determine approver type
      const quotationTotal = parseFloat(req.quotation_total?.toString().replace(/[^\d.-]/g, '') || 0);
      const quotationVat = parseFloat(req.quotation_vat?.toString().replace(/[^\d.-]/g, '') || 0);
      const netAmount = quotationTotal - quotationVat;
      const siteLimit = parseFloat(dashboardData?.site_limit || 20000);
      
      if (netAmount > siteLimit) {
        // High value: SAP approver
        const delegatedTo = normalizeUserId(req.sap_delegated_to_user_id) ?? normalizeUserId(req.sap_delegate);
        const effectiveApprover = normalizeUserId(req.sap_effective_user_id) ?? delegatedTo ?? normalizeUserId(req.approval);
        return !!effectiveApprover && effectiveApprover === userId;
      } else {
        // Standard value: PM role (role 4)
        return user.role === 4 || user.role === '4';
      }
    }
    
    return false;
  };

  // Helper function to check if approver is waiting for more information from requester
  const isApproverWaitingForInfo = (req) => {
    if (!user || !user.user_id) return false;
    
    // Must be the current approver for this to matter
    if (!isUserCurrentApprover(req)) return false;
    
    // Check if there's a last comment by this approver
    const userId = String(user.user_id);
    const lastCommentUser = req.last_comment_user ? String(req.last_comment_user) : null;
    const lastCommentApproved = req.last_comment_approved;
    const lastCommentText = req.last_comment_text || '';
    
    // Return true if:
    // 1. Last comment was made by current user (approver)
    // 2. Last comment was NOT an approval (comment_approved = 0)
    // 3. Last comment was NOT a rejection (doesn't contain rejection keywords)
    if (lastCommentUser === userId && 
        Number(lastCommentApproved) === 0 && 
        !lastCommentText.toLowerCase().includes('rejection reason:') &&
        !lastCommentText.toLowerCase().includes('rejected')) {
      return true;
    }
    
    return false;
  };

  // Get requisition approvals from dashboardData (provided by backend)
  const requisitionApprovals = dashboardData.awaiting_my_approvals || [];
  const requisitionApprovalsCount = dashboardData.awaiting_my_approvals_count || 0;

  // Helper function to get approval stage label
  const getApprovalStage = (requisitionStatus) => {
    switch (requisitionStatus) {
      case 0: return 'First Approval';
      case 1: return 'Second Approval';
      case 2: return 'Final Approval';
      default: return 'Pending';
    }
  };

  // Helper function to get helpdesk approval level label
  const getHelpdeskApprovalLevel = (level) => {
    switch (level) {
      case 1: return 'Manager';
      case 2: return 'HOD';
      case 3: return 'IT HOD';
      case 4: return 'Director';
      default: return 'Approval';
    }
  };

  // Helper function to get priority badge
  const getPriorityBadge = (priority) => {
    const badges = {
      'Critical': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
      'High': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
      'Medium': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
      'Low': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' }
    };
    return badges[priority] || badges['Medium'];
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div style={{background: 'var(--gradient-primary)'}} className="rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1 text-white">HOD Dashboard</h1>
            <p className="text-white opacity-90 text-sm">Manage your department's approvals and requests</p>
          </div>
          <div className="hidden md:block">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm">
              <div className="text-white text-xs opacity-75">Welcome back,</div>
              <div className="text-white font-semibold">{user?.user_full_name}</div>
              <div className="text-white text-xs opacity-75">{user?.department_name || ''}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Only approval related */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      {/* Helpdesk Approvals */}
      <Link to="/dashboard/helpdesk?filter=my_approvals">
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#dc3545'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#f8d7da'}}>
            <FiAlertTriangle className="w-5 h-5" style={{color: '#dc3545'}} />
          </div>
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-bold" style={{color: 'var(--color-danger)'}}>{Array.isArray(helpdeskApprovals) ? helpdeskApprovals.length : 0}</span>
              <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Helpdesk Approvals</span>
            </div>
        </div>
        </Link>

        {/* Asset Declaration Approvals - Always show, but display 0 if feature is disabled */}
        <Link to="/dashboard/assets/hod-approvals">
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#6366f1'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e0e7ff'}}>
            <FiPackage className="w-5 h-5" style={{color: '#6366f1'}} />
          </div>
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-bold" style={{color: '#6366f1'}}>{hodApprovalEnabled && Array.isArray(assetDeclarationApprovals) ? assetDeclarationApprovals.length : 0}</span>
              <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Asset Declarations</span>
            </div>
        </div>
        </Link>

        {/* Urgent Items - Hidden for now, will be conditionally displayed based on settings */}
        {/* 
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#ff6b6b'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#ffe5e5'}}>
            <FiXCircle className="w-5 h-5" style={{color: '#ff6b6b'}} />
          </div>
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-bold" style={{color: '#ff6b6b'}}>{Array.isArray(helpdeskApprovals) ? helpdeskApprovals.filter(h => h.priority === 'high').length : 0}</span>
              <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Urgent Items</span>
            </div>
        </div>
        */}

        {/* Requisition Approvals */}
        <Link to="/dashboard/requisitions?status=awaiting_my_approvals">
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#ffc107'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#fff3cd'}}>
            <FiClipboard className="w-5 h-5" style={{color: '#ffc107'}} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-2xl font-bold" style={{color: 'var(--color-warning)'}}>{requisitionApprovalsCount}</span>
            <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Requisition Approvals</span>
          </div>
        </div>
        </Link>

        {/* Total Pending */}
        <div className="bg-white rounded-xl p-3 shadow-md border-l-4 hover:shadow-lg transition-all flex items-center gap-3" style={{borderLeftColor: '#0086D4'}}>
          <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{backgroundColor: '#e3f2fd'}}>
            <FiClock className="w-5 h-5" style={{color: '#0086D4'}} />
          </div>
            <div className="flex flex-col leading-tight">
              <span className="text-2xl font-bold" style={{color: 'var(--color-primary)'}}>{requisitionApprovalsCount + (Array.isArray(helpdeskApprovals) ? helpdeskApprovals.length : 0) + (hodApprovalEnabled && Array.isArray(assetDeclarationApprovals) ? assetDeclarationApprovals.length : 0)}</span>
              <span className="text-[10px] font-semibold tracking-wide text-gray-600 uppercase">Total Pending</span>
            </div>
        </div>

      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column - Helpdesk Approvals */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-red-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#f8d7da'}}>
                  <FiAlertTriangle className="w-3.5 h-3.5" style={{color: '#dc3545'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Helpdesk Approvals
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">{Array.isArray(helpdeskApprovals) ? helpdeskApprovals.length : 0} pending</p>
                </div>
              </div>
              <Link
                to="/dashboard/helpdesk?filter=my_approvals"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>

          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {loadingHelpdesk ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm" style={{color: 'var(--text-secondary)'}}>Loading approvals...</p>
              </div>
            ) : !Array.isArray(helpdeskApprovals) || helpdeskApprovals.length === 0 ? (
              <div className="p-8 text-center">
                <FiCheckCircle className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-success)'}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No pending helpdesk approvals</p>
                <p className="text-xs mt-1" style={{color: 'var(--text-secondary)'}}>All caught up!</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                {Array.isArray(helpdeskApprovals) && helpdeskApprovals.map((ticket) => {
                  const priorityBadge = getPriorityBadge(ticket.priority);
                  
                  // Determine current approval level from ticket status fields
                  let currentLevel = 1; // Default to Manager
                  const approvalStatus = {
                    manager: parseInt(ticket.ticket_manager || 0),
                    hod: parseInt(ticket.ticket_hod || 0),
                    it_hod: parseInt(ticket.ticket_it_hod || 0),
                    director: parseInt(ticket.ticket_director || 0)
                  };
                  
                  if (approvalStatus.manager === 0) {
                    currentLevel = 1; // Manager
                  } else if (approvalStatus.hod === 0) {
                    currentLevel = 2; // HOD
                  } else if (approvalStatus.it_hod === 0) {
                    currentLevel = 3; // IT HOD
                  } else if (approvalStatus.director === 0) {
                    currentLevel = 4; // Director
                  }
                  
                  return (
                    <div key={ticket.ticket_id} className="p-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all border-l-4 border-transparent hover:border-red-400">
                      <Link to={`/dashboard/helpdesk?ticket=${ticket.ticket_id}&filter=my_approvals`} className="block">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-sm" style={{color: 'var(--text-primary)'}}>
                                #{ticket.ticket_id}
                              </span>
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${priorityBadge.bg} ${priorityBadge.text}`}>
                                {ticket.priority || 'Medium'}
                              </span>
                              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                                {getHelpdeskApprovalLevel(currentLevel)}
                              </span>
                            </div>
                            <p className="text-sm font-semibold mb-1 line-clamp-1" style={{color: 'var(--text-primary)'}}>
                              {ticket.title || ticket.subject || 'No subject'}
                            </p>
                           
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t" style={{borderColor: 'var(--border-light)'}}>
                          <span className="truncate">👤 {ticket.requester_name || ticket.creator_name || 'Unknown'}</span>
                          <span className="flex items-center gap-1 ml-2">
                            <FiClock className="w-3 h-3" />
                            {formatDate(ticket.created_at)}
                          </span>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Requisition Approvals */}
        <div className="bg-white rounded-xl shadow-lg border" style={{borderColor: 'var(--border-light)'}}>
          <div className="px-2 py-1 border-b bg-gradient-to-r from-amber-50 to-white" style={{borderColor: 'var(--border-light)'}}>
            <div className="flex items-baseline justify-between pt-1 pb-0">
              <div className="flex items-baseline gap-2">
                <div className="w-6 h-6 rounded-md flex items-center justify-center mt-0.5" style={{backgroundColor: '#fff3cd'}}>
                  <FiClipboard className="w-3.5 h-3.5" style={{color: '#ffc107'}} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold leading-tight" style={{color: 'var(--text-primary)'}}>
                    Requisition Approvals
                  </h3>
                  <p className="text-[10px] text-gray-500 leading-tight">{requisitionApprovalsCount} pending</p>
                </div>
              </div>
              <Link
                to="/dashboard/requisitions?status=awaiting_my_approvals"
                className="text-[11px] font-semibold hover:underline px-2 py-0.5 rounded-md hover:bg-gray-100 transition-colors"
                style={{color: 'var(--color-primary)'}}>
                View All →
              </Link>
            </div>
          </div>

          <div className="divide-y" style={{borderColor: 'var(--border-light)'}}>
            {!requisitionApprovals || requisitionApprovals.length === 0 ? (
              <div className="p-8 text-center">
                <FiCheckCircle className="w-12 h-12 mx-auto mb-3" style={{color: 'var(--color-success)'}} />
                <p className="text-sm font-medium" style={{color: 'var(--text-secondary)'}}>No pending requisition approvals</p>
                <p className="text-xs mt-1" style={{color: 'var(--text-secondary)'}}>All caught up!</p>
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-700">
                      <th className="font-semibold px-3 py-2 text-left">Number</th>
                      <th className="font-semibold px-3 py-2 text-left">Project</th>
                      <th className="font-semibold px-3 py-2 text-left">Department</th>
                      <th className="font-semibold px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requisitionApprovals.map((req, index) => (
                      <tr
                        key={index}
                        className={`border-b transition hover:bg-amber-50 ${
                          isApproverWaitingForInfo(req) 
                            ? 'bg-yellow-100 border-l-4 border-l-yellow-500' 
                            : ''
                        }`}
                      >
                        <td className="px-3 py-2 font-bold text-primary">
                          EPR-{req.requisition_number}
                        </td>
                        <td className="px-3 py-2">
                          {req.specification_title || 'Untitled Project'}
                        </td>
                        <td className="px-3 py-2">
                          {req.department_name}
                        </td>
                        <td className="px-3 py-2">
                          <Link 
                            to={`/dashboard/requisitions/${req.requisition_id}`} 
                            className={`px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity inline-block text-white ${
                              isApproverWaitingForInfo(req) 
                                ? 'bg-yellow-600' 
                                : 'bg-gray-600'
                            }`}
                          >
                            {isApproverWaitingForInfo(req) ? 'Awaiting Response' : 'Review'}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

export default HODDashboard;
